import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Message Sent Successfully!',
          description: data.message || 'Thank you for contacting us. We will get back to you soon.',
          duration: 5000
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again later or contact us directly at a.katoch@total-vision.com',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Our Location',
      info: 'Ahmed Ghanem Mazroui Building, Office No. 1001, 10th Floor, Hamdan Bin Mohammed Street, Al Zahiyah – E15 – Abu Dhabi, UAE',
      link: 'https://www.google.com/maps/place/Total+Vision+Oil+%26+Gas+Fields+Services+%26+Consultancy+LLC/@24.497278,54.374746,12z/data=!4m6!3m5!1s0x3e5e43bbdc93c851:0x6ecc4c068bac796f!8m2!3d24.4972782!4d54.3747465!16s%2Fg%2F11trq6bfhc?hl=en-US&entry=ttu&g_ep=EgoyMDI2MDIxNy4wIKXMDSoASAFQAw%3D%3D'
    },
    {
      icon: Mail,
      title: 'Email Us',
      info: 'info@total-vision.com',
      link: 'mailto:info@total-vision.com'
    },
    {
      icon: Phone,
      title: 'Call Us',
      info: ['+971 2 884 1675', '+971 56 905 0656'],
      links: ['tel:+97128841675', 'tel:+971569050656']
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_precision-vision/artifacts/6nho0zr1_TV%20Logo%20New_Final.png" 
              alt="Total Vision Logo" 
              className="h-24 w-auto bg-white rounded-lg p-2 shadow-md"
            />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Get In <span className="text-red-600">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6" />
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Total Vision Oil and Gas Fields Services and Consultancy LLC - SPC
          </p>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mt-2">
            Have a project in mind? Let's discuss how we can help bring your vision to life
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {contactInfo.map((item, index) => (
            <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                {Array.isArray(item.info) ? (
                  <div className="space-y-1">
                    {item.info.map((phone, idx) => (
                      <a
                        key={idx}
                        href={item.links[idx]}
                        className="block text-slate-600 hover:text-blue-600 transition-colors"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                ) : item.link ? (
                  <a
                    href={item.link}
                    className="text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    {item.info}
                  </a>
                ) : (
                  <p className="text-slate-600">{item.info}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="border-none shadow-2xl bg-white">
            <CardContent className="p-8 md:p-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="+971 2 884 1675"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                      Company
                    </label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="Your Company"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Your Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full min-h-[150px]"
                    placeholder="Tell us about your project..."
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={isSubmitting}
                >
                  <Send className="mr-2 w-5 h-5" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Google Maps Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">Find Us on the Map</h3>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d57702.36764!2d54.374746!3d24.497278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e43bbdc93c851%3A0x6ecc4c068bac796f!2sTotal%20Vision%20Oil%20%26%20Gas%20Fields%20Services%20%26%20Consultancy%20LLC!5e0!3m2!1sen!2sae!4v1709123456789!5m2!1sen!2sae"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Total Vision Office Location"
            />
          </div>
          <div className="text-center mt-6">
            <a
              href="https://www.google.com/maps/place/Total+Vision+Oil+%26+Gas+Fields+Services+%26+Consultancy+LLC/@24.497278,54.374746,12z/data=!4m6!3m5!1s0x3e5e43bbdc93c851:0x6ecc4c068bac796f!8m2!3d24.4972782!4d54.3747465!16s%2Fg%2F11trq6bfhc?hl=en-US&entry=ttu&g_ep=EgoyMDI2MDIxNy4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl"
            >
              <MapPin className="w-5 h-5" />
              Open in Google Maps
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;